import {
  BenefitCategoryGroupListResponse,
  BenefitDetailResponse,
  BenefitEngagementType,
  BenefitListResponse,
  BenefitStatus,
  benefitsService,
  CreateBenefitRequest,
  FetchBenefitsRequest,
  PublishBenefitsRequest,
  PublishBenefitsResponse,
  ReorderBenefitsRequest,
  UpdateBenefitRequest,
} from '@aslanpay/portal-common-lib';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import FileProcessingApiHandler from 'app/services/fileProcessingApiHandler';

import { FileUploadType } from '../useFileProcessingService';

const fetchBenefitsWithConcurrencyLimit = async (
  companyId: string,
  ids: string[]
): Promise<BenefitDetailResponse[]> => {
  const promises = [];
  for (let i = 0; i < ids.length; i += 1) {
    promises.push(benefitsService.fetchBenefit(companyId, ids[i]));
  }
  return Promise.all(promises);
};

const useFetchDetailsBenefits = (companyId: string | undefined, ids: string[], enabled = true) => {
  return useQuery<BenefitDetailResponse[], Error>({
    queryKey: ['fetchBenefitsDetails', { companyId, ids }],
    queryFn: async () => {
      if (!companyId) {
        throw new Error('companyId is required');
      }
      return fetchBenefitsWithConcurrencyLimit(companyId, ids);
    },
    enabled: enabled && !!companyId && ids.length > 0,
  });
};

const useFetchBenefits = (
  companyId: string | undefined,
  params?: FetchBenefitsRequest,
  enabled = true
) => {
  return useQuery<BenefitListResponse, Error>({
    queryKey: ['fetchBenefits', { companyId, ...params }],
    queryFn: () => {
      if (!companyId) {
        throw new Error('companyId is required');
      }

      return benefitsService.fetchBenefits(companyId, params);
    },
    enabled: enabled && !!companyId,
    staleTime: 30 * 1000,
    keepPreviousData: true,
  });
};

const useFetchBenefitsGroupedByCategory = (
  companyId: string | undefined,
  params?: Omit<FetchBenefitsRequest, 'groupByCategory'>,
  enabled = true
) => {
  return useQuery<BenefitCategoryGroupListResponse, Error>({
    queryKey: ['fetchBenefitsGroupedByCategory', { companyId, ...params }],
    queryFn: () => {
      if (!companyId) {
        throw new Error('companyId is required');
      }

      return benefitsService.fetchBenefitsGroupedByCategory(companyId, params);
    },
    enabled: enabled && !!companyId,
  });
};

const useFetchBenefit = (
  companyId: string | undefined,
  benefitId: string | undefined,
  enabled = true
) => {
  return useQuery<BenefitDetailResponse, Error>({
    queryKey: ['fetchBenefit', { companyId, benefitId }],
    queryFn: () => {
      if (!companyId) {
        throw new Error('companyId is required');
      }

      if (!benefitId) {
        throw new Error('benefitId is required');
      }

      return benefitsService.fetchBenefit(companyId, benefitId);
    },
    enabled: enabled && !!companyId && !!benefitId,
  });
};

const useToggleBenefitFavourite = (companyId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ benefitId, isFavourited }: { benefitId: string; isFavourited: boolean }) => {
      if (!companyId) throw new Error('companyId is required');

      return isFavourited
        ? benefitsService.unfavouriteBenefit(companyId, benefitId)
        : benefitsService.favouriteBenefit(companyId, benefitId);
    },
    onSuccess: (_data, { benefitId }) => {
      queryClient.invalidateQueries({ queryKey: ['fetchBenefits'] });
      queryClient.invalidateQueries({ queryKey: ['fetchBenefitsGroupedByCategory'] });
      queryClient.invalidateQueries({ queryKey: ['fetchBenefit', { companyId, benefitId }] });
    },
  });
};

const useRecordBenefitEngagement = (companyId: string | undefined) => {
  return useMutation({
    mutationFn: ({
      benefitId,
      engagementType,
    }: {
      benefitId: string;
      engagementType: BenefitEngagementType;
    }) => {
      if (!companyId) throw new Error('companyId is required');
      return benefitsService.recordBenefitEngagement(companyId, benefitId, engagementType);
    },
  });
};

const useCreateBenefit = (companyId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateBenefitRequest) => {
      if (!companyId) throw new Error('companyId is required');

      return benefitsService.createBenefit(companyId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchBenefits', { companyId }] });
    },
  });
};

const useUpdateBenefit = (companyId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ benefitId, body }: { benefitId: string; body: UpdateBenefitRequest }) => {
      if (!companyId || !benefitId) throw new Error('companyId and benefitId are required');

      return benefitsService.updateBenefit(companyId, benefitId, body);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['fetchBenefits', { companyId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['fetchBenefitsGroupedByCategory', { companyId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['fetchBenefit', { companyId, benefitId: variables.benefitId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['fetchBenefitsDetails'],
      });
    },
  });
};

const useUpdateBenefitStatus = (companyId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ benefitId, status }: { benefitId: string; status: BenefitStatus }) => {
      if (!companyId) throw new Error('companyId is required');

      const benefit = await benefitsService.fetchBenefit(companyId, benefitId);

      const body: UpdateBenefitRequest = {
        name: benefit.name,
        shortDescription: benefit.shortDescription,
        description: benefit.description,
        highlights: benefit.highlights.map((h) => ({ text: h.text, displayOrder: h.displayOrder })),
        logoUrl: benefit.logoUrl,
        images: benefit.images.map((i) => ({ imageUrl: i.imageUrl, displayOrder: i.displayOrder })),
        videoUrls: benefit.videoUrls.map((v) => ({ url: v.url, displayOrder: v.displayOrder })),
        linkUrl: benefit.linkUrl,
        linkName: benefit.linkName,
        categoryIds: benefit.categories.map((c) => c.id),
        faqs: benefit.faqs.map((f) => ({
          question: f.question,
          answer: f.answer,
          displayOrder: f.displayOrder,
        })),
        links: (benefit.links ?? []).map((l) => ({
          id: l.id,
          linkUrl: l.linkUrl,
          linkName: l.linkName,
          displayOrder: l.displayOrder,
        })),
        documents: (benefit.documents ?? []).map((d) => ({
          id: d.id,
          fileUploadId: d.fileUploadId,
          displayName: d.displayName,
          displayOrder: d.displayOrder,
        })),
        eligibilityCriteria: (benefit.eligibilityCriteria ?? []).map((c) => ({
          id: c.id,
          text: c.text,
          displayOrder: c.displayOrder,
        })),
        status,
      };

      return benefitsService.updateBenefit(companyId, benefitId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchBenefits', { companyId }] });
    },
  });
};

const uploadSingleFile = async (
  file: File,
  uploadType: FileUploadType,
  companyId: string
): Promise<string> => {
  const { url } = await FileProcessingApiHandler.getUploadPreSignedUrl(
    file.name,
    companyId,
    uploadType
  );

  const response = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file to S3: ${response.statusText}`);
  }

  return `${new URL(url).origin}${new URL(url).pathname}`;
};

const useUploadBenefitAsset = (companyId: string | undefined) => {
  const uploadFile = async (file: File, uploadType: FileUploadType): Promise<string> => {
    if (!companyId) throw new Error('companyId is required');
    return uploadSingleFile(file, uploadType, companyId);
  };

  return { uploadFile };
};

const useReorderBenefitsInCategory = (companyId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ categoryId, body }: { categoryId: string; body: ReorderBenefitsRequest }) => {
      if (!companyId) throw new Error('companyId is required');
      return benefitsService.reorderBenefitsInCategory(companyId, categoryId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchBenefitCategories'] });
    },
  });
};

const useDeleteBenefit = (companyId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (benefitId: string) => {
      if (!companyId) throw new Error('companyId is required');
      return benefitsService.deleteBenefit(companyId, benefitId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchBenefits', { companyId }] });
    },
  });
};

const usePublishBenefits = (companyId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation<PublishBenefitsResponse, Error, PublishBenefitsRequest>({
    mutationFn: (body: PublishBenefitsRequest) => {
      if (!companyId) throw new Error('companyId is required');
      return benefitsService.publishBenefits(companyId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchBenefits', { companyId }] });
    },
  });
};

const useRestoreBenefit = (companyId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (benefitId: string) => {
      if (!companyId) throw new Error('companyId is required');
      return benefitsService.restoreBenefit(companyId, benefitId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchBenefits', { companyId }] });
    },
  });
};

const useBenefits = {
  useFetchDetailsBenefits,
  useFetchBenefits,
  useFetchBenefitsGroupedByCategory,
  useFetchBenefit,
  useToggleBenefitFavourite,
  useRecordBenefitEngagement,
  useCreateBenefit,
  useUpdateBenefitStatus,
  useUploadBenefitAsset,
  useUpdateBenefit,
  useReorderBenefitsInCategory,
  useDeleteBenefit,
  useRestoreBenefit,
  usePublishBenefits,
};

export default useBenefits;
